import type { LocaleMessages } from '@/global'
import { Settings } from 'luxon'
import i18n from '@/i18n'

export const loadLocales = async (...namespaces: string[]) => {
  const _locale = i18n.global.locale.value
  Settings.defaultLocale = _locale
  const _messages = i18n.global.getLocaleMessage(_locale) as LocaleMessages

  // Check which namespaces need loading - either missing completely or incomplete
  const _loading = namespaces.filter((n) => {
    const existing =
      _messages[convertNamespaceToI18nNamespace(n) as keyof LocaleMessages]

    // If namespace doesn't exist, it needs loading
    if (!existing) {
      return true
    }

    // Special check for journal namespace - if it exists but doesn't have 'form' property, it's incomplete
    if (
      n === 'journal' &&
      existing &&
      typeof existing === 'object' &&
      !('form' in existing)
    ) {
      return true
    }

    return false
  })

  if (_loading.length === 0) {
    return
  }

  await Promise.all(
    _loading.map(async (n) => {
      let _messages

      try {
        // Try new locales folder structure first
        _messages = await import(
          `@/pages/${convertNamespaceToFolder(n)}/locales/${n}.${_locale}.json`
        )
      } catch {
        try {
          // Fallback to old structure if new structure doesn't exist
          _messages = await import(
            `@/pages/${convertNamespaceToFolder(n)}/${n}.${_locale}.json`
          )
        } catch {
          return
        }
      }

      // For journal namespace, we want to completely replace the existing incomplete one
      if (n === 'journal') {
        const currentMessages = i18n.global.getLocaleMessage(_locale)
        const newMessages = { ...currentMessages }
        newMessages[n as keyof typeof newMessages] = _messages.default
        i18n.global.setLocaleMessage(_locale, newMessages)
      } else {
        // For other namespaces, use merge
        i18n.global.mergeLocaleMessage(_locale, {
          [convertNamespaceToI18nNamespace(n)]: _messages.default
        })
      }
    })
  )
}

export const convertNamespaceToFolder = (namespace: string) => {
  // ! renaming /accounts/ to /accountSettings/ causes a loooot of changes to be committed
  // ! same w/moneyTransfer. Remove this function and rename the folders at a better time - Christian Young, 6/12/2025

  switch (namespace) {
    case 'accountSettings':
      return 'accounts'
    case 'payments':
      return 'moneyTransfer'
    case 'library':
      return 'infoLibrary'
    default:
      return namespace
  }
}

function convertNamespaceToI18nNamespace(namespace: string) {
  switch (namespace) {
    case 'library':
      return 'infoLibrary'
    default:
      return namespace
  }
}
